import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MediaThumb, SEVERITY_TONE, STATUS_LABEL } from "@/components/DamageReport";
import { ChargeDepositDialog, ITEM_LABEL } from "@/components/DamageExtras";
import { DamageTrends } from "@/components/DamageTrends";
import { InvoiceActions } from "@/components/DamageInvoice";
import { EstimatePanel } from "@/components/DamageEstimate";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";

function ReportCard({ r, staff, onChange }) {
  const [charging, setCharging] = useState(false);
  const discard = async () => {
    try { await api.delete(`/damage-reports/${r.id}`); toast.success("Draft discarded"); onChange(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const isDraft = r.status === "draft";
  const setTo = async (s) => {
    try { await api.patch(`/damage-reports/${r.id}`, { status: s }); toast.success(STATUS_LABEL[s]); onChange(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="ns-card p-5" data-testid={`damage-report-${r.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={`px-2 py-0.5 rounded-full border ${SEVERITY_TONE[r.severity]}`}>{r.severity}</span>
          {r.ai_generated && <span className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100" data-testid={`damage-ai-badge-${r.id}`}>AI draft</span>}
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700" data-testid={`damage-item-${r.id}`}>{ITEM_LABEL[r.item] || "Other"}</span>
          <span className="font-medium text-sm">{(r.property_name || "").replace(/\*/g, " · ")}</span>
          <span className="text-slate-500">{r.created_at.slice(0, 10)} · by {r.reported_by}</span>
          {r.estimated_cost ? <span className="font-mono">≈ £{r.estimated_cost}</span> : null}
        </div>
        <span className="text-xs text-slate-600" data-testid={`damage-status-${r.id}`}>{STATUS_LABEL[r.status]}</span>
      </div>
      <div className="text-xs text-slate-500 mb-2">
        Guest: {r.contact_id && staff ? <Link to={`/contacts/${r.contact_id}`} className="text-teal-700 hover:underline">{r.guest_name || "view guest"}</Link> : (r.guest_name || "unknown")} {r.stay}
      </div>
      <p className="text-sm text-slate-800 whitespace-pre-line">{r.description}</p>
      <div className="flex flex-wrap gap-2 mt-3">{r.media.map((m) => <MediaThumb key={m.id} reportId={r.id} m={m} />)}</div>
      {staff && !r.charge && (isDraft || r.status === "open") && <EstimatePanel r={r} onChange={onChange} />}
      {r.charge && (
        <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid={`damage-charge-${r.id}`}>
          £{r.charge.amount.toFixed(2)} {r.charge.method === "stripe_deposit" ? "captured from Stripe deposit" : "logged as manual deduction"} · {r.charge.charged_at.slice(0, 10)} by {r.charge.charged_by}
        </div>
      )}
      {r.charge && staff && <InvoiceActions r={r} onSent={onChange} />}
      {staff && isDraft && (
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={() => setTo("open")} data-testid={`damage-confirm-${r.id}`}
            className="text-[11px] uppercase tracking-wider px-3 py-1 rounded-full bg-slate-900 text-white hover:bg-teal-700 transition-colors">Confirm as open</button>
          <button onClick={discard} data-testid={`damage-discard-${r.id}`}
            className="text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-slate-200 hover:border-rose-500 hover:text-rose-600 transition-colors">Discard</button>
        </div>
      )}
      {staff && !isDraft && (
        <div className="flex flex-wrap gap-2 mt-4">
          {!r.charge && (
            <button onClick={() => setCharging(true)} data-testid={`damage-charge-btn-${r.id}`}
              className="text-[11px] uppercase tracking-wider px-3 py-1 rounded-full bg-slate-900 text-white hover:bg-teal-700 transition-colors inline-flex items-center gap-1"><CreditCard className="w-3 h-3" /> Charge deposit</button>
          )}
          {Object.entries(STATUS_LABEL).filter(([k]) => k !== r.status && k !== "charged_to_deposit" && k !== "draft").map(([k, l]) => (
            <button key={k} onClick={() => setTo(k)} data-testid={`damage-set-${k}-${r.id}`}
              className="text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-slate-200 hover:border-teal-600 hover:text-teal-700 transition-colors">→ {l}</button>
          ))}
        </div>
      )}
      {charging && <ChargeDepositDialog report={r} onClose={() => setCharging(false)} onDone={() => { setCharging(false); onChange(); }} />}
    </div>
  );
}

export default function DamageReports() {
  const { user } = useAuth();
  const [tab, setTab] = useState("reports");
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const staff = user?.role === "owner" || user?.role === "admin";
  const reqId = useRef(0);
  const load = () => {
    const id = ++reqId.current;
    return api.get("/damage-reports", { params: status ? { status } : {} }).then((r) => { if (id === reqId.current) setRows(r.data); }).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  return (
    <Layout title="Damage reports" subtitle={staff ? "Guest damage logged by cleaners at turnover — review evidence and decide on the deposit." : "Damage you've reported at turnovers."}>
      {staff && (
        <div className="flex gap-6 border-b border-slate-200 mb-6" data-testid="damage-tabs">
          {[["reports", "Reports"], ["trends", "Trends"]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`damage-tab-${v}`}
              className={`pb-2 text-sm -mb-px border-b-2 transition-colors ${tab === v ? "border-teal-700 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>
      )}
      {tab === "trends" && staff ? <DamageTrends /> : (
        <>
          <div className="flex gap-2 mb-6" data-testid="damage-filter">
            {[["draft", "Drafts"], ["open", "Open"], ["charged_to_deposit", "Charged to deposit"], ["resolved", "Resolved"], ["", "All"]].map(([v, l]) => (
              <button key={l} onClick={() => setStatus(v)} data-testid={`damage-filter-${v || "all"}`}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${status === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          {rows.length === 0 && <div className="text-sm text-slate-400 italic" data-testid="damage-empty">No damage reports here.</div>}
          <div className="space-y-4">{rows.map((r) => <ReportCard key={r.id} r={r} staff={staff} onChange={load} />)}</div>
        </>
      )}
    </Layout>
  );
}
